const validStatuses = new Set(["upcoming", "present", "absent", "missed"])
const MAX_SAMPLING_MOMENTS = 360

export function normalizeMomentaryConfig(config = {}) {
	return {
		...config,
		trackerKind: "momentarySampling",
		title: config.title ?? "Momentary Time Sampling",
		instructions: config.instructions ?? "At each prompt, score whether the behavior is occurring at that exact moment.",
		behaviorLabel: config.behaviorLabel ?? "Target behavior",
		behaviorDefinition: config.behaviorDefinition ?? "",
		context: config.context ?? "",
		contextLabel: config.contextLabel ?? "Observation context",
		prompt: config.prompt ?? "Is the target behavior occurring at this exact moment?",
		waitingLabel: config.waitingLabel ?? "Waiting for the next sampling moment",
		upcomingLabel: config.upcomingLabel ?? "Upcoming",
		missedLabel: config.missedLabel ?? "Missed moment",
		completedLabel: config.completedLabel ?? "Sampling session complete",
		interruptedLabel: config.interruptedLabel ?? "Sampling session interrupted",
		provisionalLabel: config.provisionalLabel ?? "Provisional",
		unavailableLabel: config.unavailableLabel ?? "Unavailable",
		reviewLabel: config.reviewLabel ?? "Sampled occurrence estimate",
		reviewNotesLabel: config.reviewNotesLabel ?? "Private practitioner notes",
		scheduleMode: config.scheduleMode ?? "fixed",
		intervalSeconds: Number(config.intervalSeconds ?? 30),
		durationSeconds: Number(config.durationSeconds ?? 1800),
		responseWindowSeconds: Number(config.responseWindowSeconds ?? 10),
		moments: Array.isArray(config.moments) ? config.moments.map(moment => ({...moment})) : [],
		options: Array.isArray(config.options) ? config.options.map(option => ({...option})) : [
			{id: "present", title: "Present", status: "present"},
			{id: "absent", title: "Not present", status: "absent"},
		],
		archivedOptions: Array.isArray(config.archivedOptions) ? config.archivedOptions.map(option => ({...option})) : [],
	}
}

export function getMomentaryConfigErrors(config = {}) {
	const current = normalizeMomentaryConfig(config)
	const offsets = getMomentarySchedule(current).map(moment => moment.offsetSeconds)
	if (current.scheduleMode === "fixed" && current.intervalSeconds > 0 && current.durationSeconds / current.intervalSeconds > MAX_SAMPLING_MOMENTS) return [`Limit the schedule to ${MAX_SAMPLING_MOMENTS} moments`]
	if (current.scheduleMode === "explicit" && current.moments.length > MAX_SAMPLING_MOMENTS) return [`Limit the schedule to ${MAX_SAMPLING_MOMENTS} moments`]
	if (!current.title || !current.prompt || !current.behaviorLabel || !current.contextLabel) return ["Complete the sampling labels and prompt"]
	if (!Number.isFinite(current.responseWindowSeconds) || current.responseWindowSeconds <= 0) return ["Set a positive response window"]
	if (!["fixed", "explicit"].includes(current.scheduleMode)) return ["Choose a sampling schedule type"]
	if (current.scheduleMode === "fixed" && (!Number.isFinite(current.intervalSeconds) || current.intervalSeconds <= 0 || !Number.isFinite(current.durationSeconds) || current.durationSeconds <= 0)) return ["Set a positive interval and observation length"]
	if (offsets.some(offset => !Number.isFinite(offset) || offset <= 0) || new Set(offsets).size !== offsets.length) return ["Set distinct sampling moments after the session starts"]
	const sortedOffsets = [...offsets].sort((a, b) => a - b)
	if (sortedOffsets.some((offset, index) => index > 0 && offset - sortedOffsets[index - 1] < current.responseWindowSeconds)) return ["Sampling response windows must not overlap"]
	if (current.scheduleMode === "explicit" && (current.moments.some(moment => !moment.id) || new Set(current.moments.map(moment => moment.id)).size !== current.moments.length)) return ["Set distinct moment identifiers"]
	if (!current.options.some(option => option.status === "present") || !current.options.some(option => option.status === "absent") || new Set(current.options.map(option => option.id)).size !== current.options.length || !current.options.every(option => option.id && option.title && ["present", "absent"].includes(option.status))) return ["Set present and not-present response options"]
	return []
}

export function getMomentarySchedule(config = {}) {
	const current = normalizeMomentaryConfig(config)
	if (current.scheduleMode === "explicit") return current.moments.map(moment => ({id: String(moment.id || ""), offsetSeconds: Number(moment.offsetSeconds)}))
	if (current.scheduleMode !== "fixed" || current.intervalSeconds <= 0 || current.durationSeconds <= 0) return []
	const count = Math.min(Math.floor(current.durationSeconds / current.intervalSeconds), MAX_SAMPLING_MOMENTS)
	return Array.from({length: count}, (_, index) => ({id: `fixed-${index + 1}`, offsetSeconds: current.intervalSeconds * (index + 1)}))
}

export function normalizeMomentaryPayload(payload = {}, config = {}) {
	const tracker = config.trackerKind === "momentarySampling" ? normalizeMomentaryConfig(config) : normalizeMomentaryConfig(payload.tracker || config)
	const session = {...(payload.session || {})}
	const previousEntries = payload.entries || []
	const existing = new Map(previousEntries.map(entry => [entry.id, entry]))
	const entries = session.startedAt ? getMomentarySchedule(tracker).map(moment => {
		const previous = existing.get(moment.id) || {}
		const matched = previous.offsetSeconds === moment.offsetSeconds ? previous : {}
		const currentOption = tracker.options.find(option => option.id === matched.optionId)
		const effectiveStatus = currentOption?.status || matched.status
		return {...matched, id: moment.id, offsetSeconds: moment.offsetSeconds, scheduledAt: Number(session.startedAt) + moment.offsetSeconds * 1000,
			status: validStatuses.has(effectiveStatus) ? effectiveStatus : "upcoming"}
	}) : []
	const activeKeys = new Set(entries.map(entry => `${entry.id}:${entry.offsetSeconds}`))
	const archivedEntries = [...(payload.archivedEntries || []), ...previousEntries.filter(entry => !activeKeys.has(`${entry.id}:${entry.offsetSeconds}`))]
	return {...payload, tracker, session, entries, archivedEntries}
}

export function updateMomentaryStatus(payload = {}, config = {}, momentId = "", status = "upcoming", now = Date.now(), optionId = "") {
	const current = normalizeMomentaryPayload(payload, config)
	if (!validStatuses.has(status)) return current
	return {...current, entries: current.entries.map(entry => entry.id === momentId ? {...entry, status, optionId: ["present", "absent"].includes(status) ? optionId || entry.optionId || "" : "", scoredAt: status === "upcoming" ? null : entry.scoredAt || now} : entry)}
}

export function advanceMomentarySession(payload = {}, config = {}, now = Date.now()) {
	const current = normalizeMomentaryPayload(payload, config)
	if (current.session.status === "interrupted" || current.session.status === "completed") return current
	const windowMs = current.tracker.responseWindowSeconds * 1000
	return {...current, entries: current.entries.map(entry => entry.status === "upcoming" && now > entry.scheduledAt + windowMs
		? {...entry, status: "missed", scoredAt: now} : entry)}
}

export function summarizeMomentarySession(payload = {}, config = {}) {
	const current = normalizeMomentaryPayload(payload, payload.tracker ?? config)
	const recordedSchedule = {scheduleMode: current.tracker.scheduleMode, intervalSeconds: current.tracker.intervalSeconds, durationSeconds: current.tracker.durationSeconds}
	current.tracker = normalizeMomentaryConfig(config)
	const count = status => current.entries.filter(entry => entry.status === status).length
	const present = count("present"), absent = count("absent"), missed = count("missed"), remaining = count("upcoming")
	const valid = present + absent, planned = current.entries.length
	return {headline: current.tracker.reviewLabel, tracker: current.tracker, session: current.session, recordedSchedule, entries: current.entries, archivedEntries: current.archivedEntries,
		planned, present, absent, missed, remaining, valid, percentage: valid ? Math.round(present / valid * 1000) / 10 : null,
		coverage: planned ? Math.round(valid / planned * 1000) / 10 : null, provisional: remaining > 0 || current.session.status !== "completed"}
}

export function getMomentaryPayloadErrors(payload = {}, config = {}) {
	const current = normalizeMomentaryPayload(payload, config)
	const configErrors = getMomentaryConfigErrors(current.tracker)
	if (configErrors.length) return configErrors
	if (!current.session.startedAt || !Number.isFinite(Number(current.session.startedAt))) return ["Start the sampling session"]
	if (!current.session.targetBehavior?.trim() || !current.session.context?.trim()) return ["Enter the target behavior and observation context"]
	if (!current.entries.length) return ["Set sampling moments"]
	for (const entry of current.entries) {
		if (!["present", "absent"].includes(entry.status)) continue
		if (!Number.isFinite(Number(entry.scoredAt)) || entry.scoredAt < entry.scheduledAt || entry.scoredAt > entry.scheduledAt + current.tracker.responseWindowSeconds * 1000) return ["Score only at the scheduled moment"]
		if (entry.optionId) {
			const option = [...current.tracker.options, ...current.tracker.archivedOptions].find(item => item.id === entry.optionId)
			if (!option || option.status !== entry.status) return ["Invalid sampling choice"]
		}
	}
	return []
}
